// «Сегодня»: приветствие, плитки, ЧП-накладки, напоминания разобрать прошедшие и резервы, лента дня по поясам.
import { MEET, STATUS, dayCapacity, fitsLabel, isHeld, money, plural, reserveDue, ruDateShort } from '@praktika/core';
import { busyAllDay, busyTimed, ensureBusy } from '../lib/busy.ts';
import { cdot, resVars } from '../lib/colors.ts';
import { esc } from '../lib/dom.ts';
import { clientById, clientTimeLabel, dayTitle, daySessions, defaultType, groupLabel, meetLabel, meetShort, payerOf,
  statusCls, statusLabel, toMain, typeName, windowsOf, workOf, type DayItem } from '../lib/data.ts';
import { SCREENS, fab, go } from '../lib/nav.ts';
import { IC } from '../lib/sheet.ts';
import { S } from '../lib/state.ts';
import { helloHtml } from '../lib/theme.ts';
import { atOff, nowInfo, zoneColW, zoneGroups, zoneLabel, tzChipsHtml } from '../lib/zones.ts';
import type { BusyItem } from '../lib/busy.ts';
import type { FreeWindow, WorkDay } from '@praktika/core';
import { bindCommon } from './common.ts';

type Item = { t: number; kind: 'sess'; x: DayItem } | { t: number; kind: 'free'; w: FreeWindow } |
  { t: number; kind: 'lunch'; w: WorkDay } | { t: number; kind: 'busy'; b: BusyItem };

SCREENS.today = (app) => {
  const n = nowInfo(), date = n.date, ds = daySessions(date), win = windowsOf(date), work = workOf(date),
    std = defaultType();
  const occ = ds.filter((x) => isHeld(x.status));
  ensureBusy(date);
  // время по городам: главный пояс крупно первым, остальные — колонками под шапкой с названиями городов
  const groups = zoneGroups(date), mainG = groups[groups.length - 1]!, mainTz = mainG.zones[0]!, mainOff = mainG.off,
    others = groups.slice(0, -1), zw = zoneColW(groups, 48);
  const zt = (t: number) => atOff(t, mainOff);
  const tmCell = (t: number) => '<div class="tm' + (others.length ? ' multi' : '') + '"><span class="z1">' + zt(t) +
    '</span>' + others.map((g) => '<span class="z2">' + atOff(t, g.off) + '</span>').join('') + '</div>';
  const ahead = occ.filter((x) => x.start + x.dur > n.min).length;
  let debt = 0;
  for (const c of S.clients) if (!c.archived && (c.balance ?? 0) < 0) debt += toMain(-(c.balance ?? 0), c.balanceCur);
  const pending = (S.counts.review || 0) + (S.counts.receipts || 0);
  const conf = S.conflicts || [];
  const alertHtml = conf.length ? '<div class="p-alert"><div class="h">ЧП: ' + conf.length + ' ' + plural(conf.length,
    'накладка', 'накладки', 'накладок') + ' — два клиента на одно время</div>' + conf.slice(0, 5).map((k) =>
    '<button data-conf="' + esc(k.date) + '">' + esc(ruDateShort(k.date)) + ' ' + esc(k.start) + ' · ' + esc(k.aName) +
    ' и ' + esc(k.bName) + '</button>').join('') +
    (conf.length > 5 ? '<div class="more">и ещё ' + (conf.length - 5) + '</div>' : '') + '</div>' : '';
  let html = helloHtml(n) + '<div class="p-head"><span class="d">' + esc(dayTitle(date)) + '</span><span class="m">' +
    occ.length + ' ' + plural(occ.length, 'сессия', 'сессии', 'сессий') + '</span></div>' + alertHtml +
    '<div class="p-tiles">' +
    '<button class="p-tile blue" data-tab="sched"><div class="v">' + ahead + '</div><div class="l">' + plural(ahead,
      'сессия впереди', 'сессии впереди', 'сессий впереди') + '</div></button>' +
    '<button class="p-tile" data-tab="sched"><div class="v">' + dayCapacity(win) + '</div><div class="l">окна по ' +
      std.dur + '</div></button>' +
    '<button class="p-tile orange" data-tab="clients"><div class="v">' + esc(money(Math.round(debt), '')) +
      '</div><div class="l">долги, ' + esc(S.settings.main_currency) + '</div></button>' +
    '<button class="p-tile orange" data-tab="money"><div class="v">' + pending +
      '</div><div class="l">ждут действия</div></button></div>';
  const pp = S.counts.pastPlanned;
  if (pp) html += '<button class="cta" data-go="pastPlanned"><span class="ic">!</span><span class="tx"><b>' +
    pp + ' ' + plural(pp, 'прошедшая сессия', 'прошедшие сессии', 'прошедших сессий') + '</b> ' +
    plural(pp, 'ждёт', 'ждут', 'ждут') + ' статуса</span><span class="go">Отметить ›</span></button>';
  // встречи календаря, которые приложение не узнало, и клиенты, заведённые синхронизацией сами, — разобрать
  const unk = S.counts.unknown || 0, chk = S.counts.checkClients || 0;
  if (unk || chk) html += '<button class="cta" data-go="unknown"><span class="ic">?</span><span class="tx"><b>' +
    (unk ? unk + ' ' + plural(unk, 'встреча', 'встречи', 'встреч') + ' календаря ' + plural(unk, 'не узнана', 'не узнаны',
      'не узнаны') : '') + (unk && chk ? '</b>, <b>' : '') + (chk ? chk + ' ' + plural(chk, 'клиент заведён', 'клиента заведены',
      'клиентов заведены') + ' сами' : '') + '</b></span><span class="go">Разобрать ›</span></button>';
  const resDue = S.sessions.filter((x) => reserveDue(x, n.date, n.min)).length;
  if (resDue) html += '<button class="cta res" data-go="reserves"><span class="ic">?</span><span class="tx"><b>' + resDue +
    ' ' + plural(resDue, 'резерв', 'резерва', 'резервов') + '</b> ' + plural(resDue, 'ждёт', 'ждут', 'ждут') +
    ' решения</span><span class="go">Решить ›</span></button>';
  const items: Item[] = [];
  for (const x of ds) items.push({ t: x.start, kind: 'sess', x });
  for (const w of win) items.push({ t: w.fs, kind: 'free', w });
  if (work && work.lunchStart !== null && work.lunchStart !== undefined) items.push({ t: work.lunchStart, kind: 'lunch', w: work });
  for (const b of busyTimed(date)) items.push({ t: b.start, kind: 'busy', b });
  items.sort((a, b) => a.t - b.t || (a.kind === 'sess' ? -1 : 1));
  const nextId = occ.find((x) => x.start + x.dur > n.min)?.id ?? null;
  html += '<div class="tlbar top">' + tzChipsHtml() + '</div>' + (others.length ? '<div class="tzhead" style="--zw:' + zw +
    'px"><span class="m">' + zoneLabel(mainG, '<br>') + '</span>' + others.map((g) => '<span>' + zoneLabel(g, '<br>') +
    '</span>').join('') + '</div>' : '') + '<div class="p-list" style="--zw:' + zw + 'px">';
  const allDay = busyAllDay(date);
  if (allDay.length) html += '<div class="p-row bz allday"><div class="who"><b>Весь день:</b> ' +
    esc(allDay.map((b) => b.title).join(', ')) + '</div></div>';
  if (!work) html += '<div class="empty">Сегодня выходной</div>';
  else if (!items.length) html += '<div class="empty">Сессий нет, окон нет</div>';
  for (const it of items) {
    if (it.kind === 'busy') {
      // не узнанная встреча календаря сессий — тоже занято, с отметкой и переходом к разбору
      const tag = it.b.unk ? ' <span class="tag o">не узнана</span>' : '';
      html += (it.b.unk ? '<button class="p-row bz" data-go="unknown">' : '<div class="p-row bz">') + tmCell(it.b.start) +
        '<div class="who">' + esc(it.b.title) + tag + ' · до ' + zt(it.b.end) + (it.b.cal ? ' <span class="small">· ' +
        esc(it.b.cal) + '</span>' : '') + '</div>' + (it.b.unk ? '</button>' : '</div>');
      continue;
    }
    if (it.kind === 'lunch') {
      html += '<div class="p-row lunch">' + tmCell(it.w.lunchStart!) + '<div class="who">обед до ' + zt(it.w.lunchEnd!) +
        '</div></div>';
      continue;
    }
    if (it.kind === 'free') {
      html += '<button class="p-row free" data-book="' + it.w.fs + '" data-book-tz="' + esc(mainTz) + '">' +
        tmCell(it.w.fs) + '<div class="who">свободно до ' + zt(it.w.fe) + ' · ' + esc(fitsLabel(it.w.fits)) +
        '</div><div class="plus">+</div></button>';
      continue;
    }
    const x = it.x, s = x.sess, c = clientById(s.clientId), past = x.start + x.dur <= n.min;
    let info = (s.num ? '<span class="num">№' + s.num + '</span> · ' : '') + esc(groupLabel(s)) + ' · ' +
      esc(typeName(s.typeId)) + ' ' + x.dur + ' · ' + esc(meetLabel(s));
    const ct = clientTimeLabel(s, c);
    if (ct) info += ' · ' + esc(ct);
    const payer = c ? payerOf(c) : null;
    if (payer && !payer.isProject && (payer.balance ?? 0) < 0) info += ' · <span class="neg">долг ' +
      esc(money(-(payer.balance ?? 0), payer.cur)) + '</span>';
    else if (payer && !payer.isProject && (payer.balance ?? 0) > 0) info += ' · <span class="pos">аванс</span>';
    const res = s.status === STATUS.RESERVE;
    if (res) info = '<b>резерв</b> · ' + info;
    if (x.id === nextId && isHeld(x.status)) {
      html += '<div class="next" data-sess="' + esc(x.id) + '"><div class="top"><span class="nm">' + cdot(c) +
        esc(s.clientName) + '</span><span class="tm">' + zt(x.start) + '–' + zt(x.start + x.dur) +
        (others.length ? '<small>' + others.map((g) => atOff(x.start, g.off) + '–' + atOff(x.start + x.dur, g.off) + ' ' +
        zoneLabel(g)).join(' · ') + '</small>' : '') + '</span></div><div class="i">' + info +
        '</div><div class="btnrow" style="padding:0">' +
        (s.link && s.meet !== MEET.OFFLINE ? '<a class="btn pri" href="' + esc(s.link) + '" target="_blank" rel="noopener">' +
          IC.video + 'Войти в ' + esc(meetShort(s)) + '</a>' :
          '<button class="btn sec" data-sess-open="' + esc(x.id) + '">Действия</button>') +
        '<button class="btn sec" style="flex:0 0 auto" data-remind="' + esc(x.id) + '">Написать</button></div></div>';
    } else {
      html += '<button class="p-row' + (past ? ' past' : '') + (res ? ' res' : '') + '" data-sess-open="' + esc(x.id) +
        '"' + (res ? ' style="' + resVars(c, s) + '"' : '') + '>' + tmCell(x.start) + '<div class="who"><div class="n">' +
        cdot(c) + esc(s.clientName) + '</div><div class="i">' + info + '</div></div><div class="st ' +
        statusCls(s.status) + '">' + esc(statusLabel(s.status)) + '</div></button>';
    }
  }
  html += '</div>';
  app.innerHTML = html;
  bindCommon(app);
  fab('Записать', () => go('book', {}));
};
